/* eslint-disable max-lines */
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PrintIcon from '@mui/icons-material/Print';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Pagination,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { capitalize, find, includes, isEmpty, map, omit, pick, some } from 'lodash';
import moment from 'moment';
import momentTimeZone from 'moment-timezone';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import LoadingModule from 'common/components/LoadingModule';
import useInfoDialog from 'common/hooks/useInfoDialog';
import { PrescriptionReviewPayloadContext } from 'common/hooks/usePrescriptionReviewPayloadContext';
import VerifiedTag from 'common/modules/VerifiedTag';
import {
  EnableInventoryLookup,
  SELF_PRESCRIBE_WARNING,
  hasValidOverride,
  isSelfPrescribeMatchByParts,
} from 'common/utils';
import { PrescriptionReviewStatus } from 'enums/prescription.enum';
import { VerificationStatus } from 'enums/provider.enum';
import ResolveDrugProfileModal from 'modules/WorkQueue/components/ResolveDrugProfileModal';
import AssociateToCatalogModal from 'modules/WorkQueue/components/AssociateToCatalogModal';
import ResolveDirectionModal from 'modules/WorkQueue/components/ResolvePrescriptionModal';
import {
  getAllOrders,
  getPatientPrescriptionDetails,
  getPrescriptionFillHistory,
  getPrescriptionSignedUrl,
  listPatientPrescriptions,
} from 'store/thunks/patientThunks';
import { overridePrescription } from 'store/thunks/workQueueThunks';

import {
  PrescriptionFormattedStatus,
  prescriptionReviewStatus,
} from 'common/constants/prescriptionStatus';
import roles from 'common/constants/roles';
import { OrderFillType } from 'enums/common.enum';
import lineItemEnum from 'enums/lineItem.enum';
import orderEnum from 'enums/order.enum';
import ChangeReviewReasonDialog from 'modules/WorkQueue/components/changeReasonButton';
import EditFillModal from 'modules/WorkQueue/components/EditFillModal';
import EditPrescriptionModal from 'modules/WorkQueue/components/EditPrescriptionModal';
import ModifyItemButton from 'modules/WorkQueue/components/ModifyItemButton';
import MultiRxReviewDialog from 'modules/WorkQueue/components/MultiRxReviewDialog';
import ResolveAsIs from 'modules/WorkQueue/components/ResolveAsIsButton';
import ResolveCentralFillStateModal from 'modules/WorkQueue/components/ResolveCentralFillStateModal';
import ResolveShipLaterReviewModal from 'modules/WorkQueue/components/ResolveShipLaterReviewModal';
import usePartnerContactForPatient from 'modules/OrderView/hooks/usePartnerContactForPatient';

import AttestationDetailsModal from './AttestationDetailsModal';
import CopyIconButton from './CopyIconButton';
import FillHistoryDialog from './FillHistoryDialog';
import PatientPrescriptionNotes from './PatientPrescriptionNotes';
import { NoChangesBadge, OverrideBadge, StatusBadge } from './PrescriptionBadges';
import RefillButton from './RefillButton';
import PartnerContactValue from '../PartnerContacts/PartnerContactValue';

const EvidenceSection = ({
  prescriptionId,
  attestation,
  attachments,
  dispatch,
  prescription,
  originalPrescriber,
  practice,
}) => {
  const [attestationPage, setAttestationPage] = useState(1);
  const [attachmentPage, setAttachmentPage] = useState(1);
  const [openAttestationModal, setOpenAttestationModal] = useState(null);
  const itemsPerPage = 5;

  // Prepare attestation items
  const attestationItems = useMemo(() => {
    const items = [];
    if (attestation && Array.isArray(attestation)) {
      attestation.forEach((attestationItem) => {
        if (attestationItem.partnerName && attestationItem.proxyPartnerName) {
          items.push({
            ...attestationItem,
            id: `${attestationItem.partnerId || ''}-${attestationItem.proxyPartnerId || ''}`,
          });
        }
      });
    } else if (attestation) {
      // Handle legacy single object format for backward compatibility
      if (attestation.partnerName && attestation.proxyPartnerName) {
        items.push({
          ...attestation,
          id: `${attestation.partnerId || ''}-${attestation.proxyPartnerId || ''}`,
        });
      }
    }
    return items;
  }, [attestation]);

  const attestationTotalPages = useMemo(
    () => Math.ceil((attestationItems?.length || 0) / itemsPerPage),
    [attestationItems]
  );

  const paginatedAttestations = useMemo(() => {
    if (isEmpty(attestationItems)) return [];
    const startIndex = (attestationPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return attestationItems.slice(startIndex, endIndex);
  }, [attestationItems, attestationPage]);

  // Prepare attachment items
  const attachmentTotalPages = useMemo(
    () => Math.ceil((attachments?.length || 0) / itemsPerPage),
    [attachments]
  );

  const paginatedAttachments = useMemo(() => {
    if (isEmpty(attachments)) return [];
    const startIndex = (attachmentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return attachments.slice(startIndex, endIndex);
  }, [attachments, attachmentPage]);

  const handleAttestationPageChange = (event, value) => {
    setAttestationPage(value);
  };

  const handleAttachmentPageChange = (event, value) => {
    setAttachmentPage(value);
  };

  useEffect(() => {
    if (attestationTotalPages > 0 && attestationPage > attestationTotalPages) {
      setAttestationPage(attestationTotalPages);
    } else if (attestationTotalPages === 0) {
      setAttestationPage(1);
    }
  }, [attestationTotalPages, attestationPage]);

  useEffect(() => {
    if (attachmentTotalPages > 0 && attachmentPage > attachmentTotalPages) {
      setAttachmentPage(attachmentTotalPages);
    } else if (attachmentTotalPages === 0) {
      setAttachmentPage(1);
    }
  }, [attachmentTotalPages, attachmentPage]);

  const handleDownload = ({ prescriptionUUID, fileKey, fileName }) => {
    dispatch(
      getPrescriptionSignedUrl({
        prescription: { prescriptionUUID, fileKey },
      })
    ).then((response) => {
      const signedUrl = response?.[0]?.signedURL;
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = fileName;
      link.click();
    });
  };

  const hasAttestations = !isEmpty(attestationItems);
  const hasAttachments = !isEmpty(attachments);

  return (
    <>
      {/* Attestations Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, fontSize: '0.85rem' }}>
          Attestations
        </Typography>
        {hasAttestations ? (
          <>
            <Box
              sx={{
                width: '100%',
                maxHeight: hasAttestations && attestationTotalPages > 1 ? '300px' : 'auto',
                overflowY: hasAttestations && attestationTotalPages > 1 ? 'auto' : 'visible',
              }}
            >
              {paginatedAttestations.map((att) => (
                <Box
                  key={`${att.partnerId || ''}-${att.proxyPartnerId || ''}`}
                  sx={{
                    mt: 1,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    backgroundColor: 'rgba(63, 81, 181, 0.08)',
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant='body1'
                    sx={{
                      fontWeight: 500,
                      color: 'primary.main',
                      fontSize: '0.8rem',
                      flexGrow: 1,
                    }}
                  >
                    {att.proxyPartnerName} has submitted attestation for {att.partnerName}.
                  </Typography>
                  {att.attestationId && (
                    <Tooltip title='View Attestation Details' arrow placement='top'>
                      <IconButton
                        size='small'
                        onClick={() => setOpenAttestationModal(att)}
                        sx={{
                          color: 'primary.main',
                          padding: '4px',
                          '&:hover': {
                            backgroundColor: 'rgba(63, 81, 181, 0.12)',
                          },
                        }}
                      >
                        <OpenInNewIcon sx={{ fontSize: '18px' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Box>
            {attestationTotalPages > 1 && (
              <Grid container justifyContent='flex-end' sx={{ mt: 2 }}>
                <Pagination
                  count={attestationTotalPages}
                  page={attestationPage}
                  size='small'
                  onChange={handleAttestationPageChange}
                />
              </Grid>
            )}
          </>
        ) : (
          <Typography
            variant='body2'
            sx={{ fontSize: '0.85rem', color: 'text.secondary', textAlign: 'center', py: 2 }}
          >
            Nothing Found
          </Typography>
        )}
      </Box>

      {/* Attachments Section */}
      <Box>
        <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, fontSize: '0.85rem' }}>
          Attachments
        </Typography>
        {hasAttachments ? (
          <>
            <Box
              sx={{
                width: '100%',
                maxHeight: hasAttachments && attachmentTotalPages > 1 ? '300px' : 'auto',
                overflowY: hasAttachments && attachmentTotalPages > 1 ? 'auto' : 'visible',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {paginatedAttachments.map((attachment) => (
                  <Box
                    key={`attachment-${attachment.fileKey}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#F5F5F5',
                      borderRadius: '8px',
                      border: '1px solid #E0E0E0',
                      p: 1.5,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      gap: 1.5,
                    }}
                  >
                    <DescriptionIcon
                      sx={{
                        color: '#757575',
                        fontSize: '24px',
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#000000',
                        flexGrow: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '0.85rem',
                      }}
                    >
                      {attachment.fileName}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        handleDownload({
                          prescriptionUUID: prescriptionId,
                          fileKey: attachment.fileKey,
                          fileName: attachment.fileName,
                        })
                      }
                      sx={{
                        color: '#757575',
                        padding: '4px',
                        borderRadius: 0,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
            {attachmentTotalPages > 1 && (
              <Grid container justifyContent='flex-end' sx={{ mt: 2 }}>
                <Pagination
                  count={attachmentTotalPages}
                  page={attachmentPage}
                  size='small'
                  onChange={handleAttachmentPageChange}
                />
              </Grid>
            )}
          </>
        ) : (
          <Typography
            variant='body2'
            sx={{ fontSize: '0.85rem', color: 'text.secondary', textAlign: 'center', py: 2 }}
          >
            Nothing Found
          </Typography>
        )}
      </Box>

      {/* Attestation Details Modal */}
      {openAttestationModal && (
        <AttestationDetailsModal
          open={!!openAttestationModal}
          onClose={() => setOpenAttestationModal(null)}
          attestationId={openAttestationModal.attestationId}
          partnerName={openAttestationModal.partnerName}
          proxyPartnerName={openAttestationModal.proxyPartnerName}
          prescription={prescription}
          originalPrescriber={originalPrescriber}
          practice={practice}
        />
      )}
    </>
  );
};

EvidenceSection.propTypes = {
  prescriptionId: PropTypes.string.isRequired,
  prescription: PropTypes.shape({
    drug: PropTypes.string,
    strength: PropTypes.string,
    quantityPrescribed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    directions: PropTypes.string,
    refillsAuthorized: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    writtenDate: PropTypes.string,
    sig: PropTypes.shape({
      sigText: PropTypes.string,
    }),
    originalPrescriber: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      middleName: PropTypes.string,
      title: PropTypes.string,
      npi: PropTypes.string,
      phoneNumber: PropTypes.string,
      phone: PropTypes.string,
    }),
    request: PropTypes.shape({
      medication: PropTypes.shape({
        prescriber: PropTypes.shape({
          firstName: PropTypes.string,
          lastName: PropTypes.string,
          npi: PropTypes.string,
          phone: PropTypes.string,
        }),
      }),
    }),
  }),
  originalPrescriber: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    middleName: PropTypes.string,
    title: PropTypes.string,
    npi: PropTypes.string,
    phoneNumber: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
  }),
  practice: PropTypes.shape({
    prescriberName: PropTypes.string,
    prescriberNpi: PropTypes.string,
  }),
  attestation: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        partnerName: PropTypes.string,
        proxyPartnerName: PropTypes.string,
        partnerId: PropTypes.string,
        proxyPartnerId: PropTypes.string,
      })
    ),
    PropTypes.shape({
      partnerName: PropTypes.string,
      proxyPartnerName: PropTypes.string,
      partnerId: PropTypes.string,
      proxyPartnerId: PropTypes.string,
    }),
  ]),
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      fileName: PropTypes.string,
      fileKey: PropTypes.string,
    })
  ),
  dispatch: PropTypes.func.isRequired,
};

EvidenceSection.defaultProps = {
  attestation: null,
  attachments: [],
  prescription: null,
  originalPrescriber: null,
  practice: null,
};

const PatientPrescriptionView = ({
  mpi,
  prescriptionId,
  prescriptionData,
  directionReview,
  overrideDetails,
  patientData,
  isPrescriptionReview,
  reviewType,
  showTranslation,
  lineItemId,
  orders,
  isPrescriptionsTab,
  onPrint,
  onClose,
  notes,
  onAddNote,
  onRefreshOverrideDetails,
}) => {
  const userRoles = useSelector(({ auth }) => auth?.currentTenantRoles);
  const [alertDismissed, setAlertDismissed] = useState(false);
  useEffect(() => {
    setAlertDismissed(false);
  }, [prescriptionId]);
  const [resolveModal, setResolveModal] = useState(false);
  const [resolveDirection, setResolveDirection] = useState(false);
  const [showEditPrescriptionModal, setShowEditPrescriptionModal] = useState(false);
  const [showEditFillModal, setShowEditFillModal] = useState(false);
  const [showResolveMultiRxModal, setShowResolveMultiRxModal] = useState(false);
  const [showResolveCentralFillModal, setShowResolveCentralFillModal] = useState(false);
  const [isResolveShipLaterOpen, setIsResolveShipLaterOpen] = useState(false);
  const [openFillHistoryDialog, setOpenFillHistoryDialog] = useState(false);
  const [fillHistoryData, setFillHistoryData] = useState(null);
  const [loadingFillHistory, setLoadingFillHistory] = useState(false);
  const prescriptionList = useSelector(({ patient }) => patient[mpi]?.prescriptions);
  const { payload, setPayload, clearHistory } = useContext(PrescriptionReviewPayloadContext);
  const dispatch = useDispatch();
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openChangeReasonDialog, setOpenChangeReasonDialog] = useState(false);
  const [pharmacyTab, setPharmacyTab] = useState(0);
  const [transferPharmacyIndex, setTransferPharmacyIndex] = useState(0);
  const reviewReason = PrescriptionFormattedStatus[reviewType];
  const prescription = isPrescriptionReview
    ? prescriptionData
    : find(prescriptionList, { prescriptionUUID: prescriptionId });
  // The patient's email and phone as the prescription's partner has them (POM-6400).
  const prescriptionPartnerContact = usePartnerContactForPatient({
    mpi,
    partnerUuid: prescription?.partnerUuid,
  });

  useEffect(() => {
    if (
      !isPrescriptionReview &&
      prescriptionId &&
      prescription &&
      mpi &&
      (!prescription.orderId || !prescription.dateOrdered || !prescription.activeItemId)
    ) {
      dispatch(
        getPatientPrescriptionDetails({
          mpi,
          prescriptionUUID: prescriptionId,
          forceRefresh: false,
        })
      );
    }
  }, [dispatch, prescriptionId, isPrescriptionReview, prescription, mpi]);

  const orderHistory = useSelector(({ patient }) => patient[mpi]?.orderHistory || []);
  const allPatientOrders = [...(orders || []), ...orderHistory];
  const matchedOrder = find(allPatientOrders, { orderId: prescription?.orderId });

  const hasOverrideRecord = useMemo(
    () => isPrescriptionReview && hasValidOverride(prescriptionData),
    [isPrescriptionReview, prescriptionData]
  );

  const fillType = matchedOrder?.fillType;

  const isReshipment = !!matchedOrder?.reshipmentOf;
  const isOtcPrescription = prescription?.rxNumber?.includes('OTC');

  const orderData = map(orders, (order) => find(order.lineItems, { lineItemId }));

  const validOrderData = orderData.filter(Boolean);

  const firstDispensedQuantity = validOrderData.length > 0 ? validOrderData[0].quantity : null;

  const prescriptionOverride = prescription?.prescriptionOverride;
  const enableDrugEditOptions =
    reviewType === PrescriptionReviewStatus.DRUG_PROFILE_REVIEW ||
    reviewType === PrescriptionReviewStatus.PRESCRIPTION_REVIEW;

  // POM-5706: when the inventory module is enabled, a drug-profile review is resolved by
  // associating a catalog product (AssociateToCatalogModal) instead of the legacy
  // resolve/create drug-profile flow. Label-edit (PRESCRIPTION_REVIEW) keeps the existing modal.
  const showCatalogAssociation =
    EnableInventoryLookup && reviewType === PrescriptionReviewStatus.DRUG_PROFILE_REVIEW;

  const enableSigEditOptions =
    reviewType === PrescriptionReviewStatus.DIRECTION_REVIEW ||
    reviewType === PrescriptionReviewStatus.PRESCRIPTION_REVIEW;

  const originatingPharmacy = prescription?.originatingPharmacy;
  const originatingPharmacyAddress = originatingPharmacy?.pharmacyAddress;
  const showPrescriptionReview = includes(
    [
      prescriptionReviewStatus.MANUAL_ORDER_REVIEW,
      prescriptionReviewStatus.PROVIDER_CLARIFICATION_REVIEW,
      prescriptionReviewStatus.SHIPPING_ADDRESS_REVIEW,
      prescriptionReviewStatus.SHIP_LATER_REVIEW,
      prescriptionReviewStatus.OTHER_REVIEW,
    ],
    reviewType
  );

  const specialEditUserRoles = [roles.admin, roles.pharmacist];
  const hasSpecialEditUserAccess = some(userRoles, (role) => includes(specialEditUserRoles, role));

  const patientDetails = useSelector(({ patient }) => {
    return {
      ...pick(patient[mpi], [
        'firstName',
        'lastName',
        'middleName',
        'gender',
        'email',
        'mobilePhone',
        'dob',
        'mpi',
        'notes',
        'originalPartner',
      ]),
      address: find(patient[mpi]?.addresses, 'isDefault')?.address,
      qs1Id: patient[mpi]?.systemIds?.qs1PatientId,
    };
  }, shallowEqual);

  const closeResolveModal = () => setResolveModal(false);
  const closeDirectionResolveModal = () => setResolveDirection(false);
  const closeEditPrescriptionModal = () => setShowEditPrescriptionModal(false);
  const closeEditFillModal = () => setShowEditFillModal(false);
  const closeMultiRxModal = () => {
    closeResolveModal();
    setShowResolveMultiRxModal(false);
  };

  const openEditPrescriptionModal = () => setShowEditPrescriptionModal(true);
  const openEditFillModal = () => setShowEditFillModal(true);

  const hasUnsavedPrescriptionChanges =
    payload?.prescriptionDetails?.providerId !== undefined ||
    payload?.prescriptionDetails?.prescriberName !== undefined ||
    payload?.prescriptionDetails?.refills !== undefined ||
    payload?.prescriptionDetails?.prescribedQuantity !== undefined;

  const hasUnsavedFillChanges = payload?.prescriptionDetails?.dispensedQuantity !== undefined;
  const isLineItemSplit = prescription?.lineItemStatus === lineItemEnum.SPLIT;
  const isOrderExpiredOrCancelled =
    prescription?.orderStatus &&
    includes(
      [orderEnum.EXPIRED, orderEnum.CANCELED, orderEnum.TRANSFERRED],
      prescription.orderStatus
    );
  const isEditFillDisabled =
    showEditPrescriptionModal ||
    hasUnsavedPrescriptionChanges ||
    isOrderExpiredOrCancelled ||
    isLineItemSplit;
  const isEditPrescriptionDisabled = showEditFillModal || hasUnsavedFillChanges;

  const getEditFillTooltip = () => {
    if (!isEditFillDisabled) return '';
    if (isOrderExpiredOrCancelled) {
      return 'Cannot edit fill for expired, transferred or cancelled orders';
    }
    if (isLineItemSplit) {
      return 'Cannot edit fill for split line items';
    }
    if (showEditPrescriptionModal) {
      return 'Please close Edit Prescription before editing current fill';
    }
    if (hasUnsavedPrescriptionChanges) {
      return 'Please save prescription changes before editing current fill';
    }
    return '';
  };

  const getEditPrescriptionTooltip = () => {
    if (!isEditPrescriptionDisabled) return '';
    if (showEditFillModal) {
      return 'Please close Edit Current Fill before editing prescription';
    }
    if (hasUnsavedFillChanges) {
      return 'Please save fill changes before editing prescription';
    }
    return '';
  };

  const practice = prescription?.practice;
  const patientInfo = isPrescriptionReview ? patientData : patientDetails;

  const medication = prescription?.request?.medication;
  const prescriptionProvider = prescription?.originalPrescriber;
  const itemProvider = {
    ...pick(medication?.prescriber, ['firstName', 'middleName', 'lastName', 'title', 'npi', 'dea']),
    phoneNumber: medication?.prescriber?.phone,
    // POM-6354: a prescriber's address is the practice address, never the one stored on the
    // partner request. Used only when core resolved no provider (originalPrescriber is null).
    address: practice?.line1
      ? pick(practice, ['line1', 'line2', 'city', 'state', 'zip'])
      : undefined,
  };

  const prescriber = prescriptionProvider || itemProvider;

  const { sigId: editedSigId } = payload?.sig || '';
  const { drugProfileId: editedDrugProfileId } = payload?.drugProfile || '';
  const editedPrescriptionDetails = payload?.prescriptionDetails;

  // When associated with a catalog (productId), the dispense identity — medication name / strength /
  // form / unit — is sourced from the catalog (productDetails, resolved at association); otherwise
  // fall back to the drug profile, then the as-written prescription.
  const prescriptionDrug =
    prescriptionOverride?.productDetails?.drugName ||
    prescriptionOverride?.drugProfile?.drugName ||
    prescription.drug;
  const prescriptionProductId =
    prescriptionOverride?.productDetails?.productId || prescription?.productId;
  // POM-5748: drug details resolved from the fulfilled NDC (populated only once the
  // prescription is fulfilled); used to show what was actually dispensed.
  const fulfilledProduct = prescriptionOverride?.productDetails;
  const prescriptionStrength =
    prescriptionOverride?.productDetails?.strength ||
    prescriptionOverride?.drugProfile?.strength ||
    prescription.strength;
  const prescriptionForm =
    (prescriptionOverride?.productDetails?.form ||
      prescriptionOverride?.drugProfile?.form ||
      prescription?.form) ??
    'NA';
  const prescriptionUnit =
    (prescriptionOverride?.productDetails?.unit ||
      prescriptionOverride?.drugProfile?.unit ||
      prescription?.unit) ??
    'NA';
  const drugData = {
    drug: prescriptionDrug,
    productId: prescriptionProductId,
    strength: prescriptionStrength,
    form: prescriptionForm,
    unit: prescriptionUnit,
  };
  const prescriptionDirection = prescriptionOverride?.sig?.sigText || prescription?.directions;

  const originalPrescriber = prescriber;

  const overriddenPrescriber = prescription?.overriddenPrescriber || originalPrescriber;

  const getPrescriberName = (prescriberData) => {
    if (!prescriberData) return null;
    if (prescriberData.name) return prescriberData.name;
    const parts = [
      prescriberData.title,
      prescriberData.firstName,
      prescriberData.middleName,
      prescriberData.lastName,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : null;
  };

  const originalPrescriberName = getPrescriberName(originalPrescriber) || practice?.prescriberName;
  const originalNpi = originalPrescriber?.npi || practice?.prescriberNpi;
  const originalPhone = originalPrescriber?.phoneNumber;
  const originalAddress = originalPrescriber?.address;

  const overriddenPrescriberName = getPrescriberName(overriddenPrescriber);
  const overriddenNpi = overriddenPrescriber?.npi;
  const overriddenPhone = overriddenPrescriber?.phoneNumber;

  // Helper function to format address
  const formatAddress = (address) => {
    if (!address) return 'N/A';
    const parts = [address.line1];
    if (address.line2) parts.push(address.line2);
    if (address.line1 && (address.city || address.state || address.zip)) {
      const cityStateZip = [address.city, address.state, address.zip].filter(Boolean).join(' ');
      if (cityStateZip) parts.push(cityStateZip);
    }
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const prescriptionPrescriberName =
    prescriptionOverride?.prescriptionDetails?.prescriberName || originalPrescriberName;
  const prescriptionRefills =
    prescriptionOverride?.prescriptionDetails?.refills || prescription?.refillsAuthorized;
  const prescriptionQuantity =
    prescriptionOverride?.prescriptionDetails?.quantity || prescription?.quantityPrescribed;
  const dispensedQuantity =
    prescriptionOverride?.prescriptionDetails?.dispensedQuantity || prescription?.dispensedQuantity;
  const updatedDrug = payload?.drugProfile?.drugName || prescriptionDrug;
  const updatedStrength = payload?.drugProfile?.strength || prescriptionStrength;
  const updatedForm = (payload?.drugProfile?.form || prescriptionForm) ?? 'NA';
  const updatedUnit = (payload?.drugProfile?.unit || prescriptionUnit) ?? 'NA';
  const instructions = prescription?.directions || prescription?.sig;
  const updatedPrescriberName =
    payload?.prescriptionDetails?.prescriberName || prescriptionPrescriberName;
  const updatedQuantity = payload?.prescriptionDetails?.quantity || prescriptionQuantity;
  const updatedPrescriptionQuantity =
    payload?.prescriptionDetails?.dispensedQuantity || firstDispensedQuantity || dispensedQuantity;
  const updatedRefills = payload?.prescriptionDetails?.refills || prescriptionRefills;
  const providerId =
    prescriptionOverride?.prescriptionDetails?.providerId || prescription?.providerId;

  const editablePrescriptionDetails = {
    prescriberName: prescriptionPrescriberName,
    refills: prescriptionRefills,
    prescribedQuantity: prescriptionQuantity,
    dispensedQuantity: prescription?.currentFillQuantity,
  };

  const editableFillDetails = {
    prescribedQuantity: prescriptionQuantity,
    dispensedQuantity: firstDispensedQuantity || dispensedQuantity,
  };

  // A fill-quantity-only save must only send `dispensedQuantity`. Resending refills/prescriberName/
  // prescribedQuantity when nothing about the prescription itself changed makes the backend treat
  // the resent refills value as a fresh authorization and overwrite refillsRemaining (POM-5592).
  const updatedPrescriptionDetails = hasUnsavedPrescriptionChanges
    ? {
        prescriberName:
          editedPrescriptionDetails?.prescriberName || editablePrescriptionDetails.prescriberName,
        refills: editedPrescriptionDetails?.refills || editablePrescriptionDetails.refills,
        prescribedQuantity:
          editedPrescriptionDetails?.prescribedQuantity || editableFillDetails.prescribedQuantity,
        dispensedQuantity:
          editedPrescriptionDetails?.dispensedQuantity || editableFillDetails.dispensedQuantity,
      }
    : {
        dispensedQuantity:
          editedPrescriptionDetails?.dispensedQuantity || editableFillDetails.dispensedQuantity,
      };

  let currentRefill = Number(updatedRefills) - Number(prescription?.refillsRemaining);
  currentRefill = String(currentRefill).replace(/-/g, '');
  currentRefill = Number(currentRefill);
  const isFirstFillOrder = fillType === 'FIRST_FILL';

  const handleSavePrescriptionChanges = () => {
    const isPrescriptionEdit = hasUnsavedPrescriptionChanges;
    const isFillEdit = hasUnsavedFillChanges;

    const prescriptionDetailsForApi = (() => {
      if (isEmpty(editedPrescriptionDetails)) return undefined;
      const details = { ...updatedPrescriptionDetails };
      if (editedPrescriptionDetails?.providerId) {
        details.providerId = editedPrescriptionDetails.providerId;
        delete details.prescriberName;
      }
      return details;
    })();

    const overridePrescriptionPayload = {
      input: {
        prescriptionUUID: prescriptionId,
        ...(editedSigId && { sig: { sigId: editedSigId } }),
        ...(editedDrugProfileId && {
          drugProfile: {
            drugProfileId: editedDrugProfileId,
          },
        }),
        ...(prescriptionDetailsForApi && { prescriptionDetails: prescriptionDetailsForApi }),
        patientId: mpi,
        lineItemId,
      },
    };

    const changes = [];

    if (payload?.prescriptionDetails?.prescriberName !== undefined) {
      changes.push({
        field: 'Prescriber Name',
        oldValue: prescriptionPrescriberName,
        newValue: payload.prescriptionDetails.prescriberName,
      });
    }

    if (payload?.prescriptionDetails?.refills !== undefined) {
      changes.push({
        field: 'Refills',
        oldValue: prescriptionRefills,
        newValue: payload.prescriptionDetails.refills,
      });
    }

    if (payload?.prescriptionDetails?.prescribedQuantity !== undefined) {
      changes.push({
        field: 'Prescribed Quantity',
        oldValue: prescriptionQuantity,
        newValue: payload.prescriptionDetails.prescribedQuantity,
      });
    }

    if (payload?.prescriptionDetails?.dispensedQuantity !== undefined) {
      changes.push({
        field: 'Dispensed Quantity',
        oldValue: firstDispensedQuantity || dispensedQuantity,
        newValue: payload.prescriptionDetails.dispensedQuantity,
      });
    }

    const changeMessage =
      changes.length > 0 ? (
        <Box>
          <Typography variant='body1' sx={{ mb: 2 }}>
            Please confirm that you have reviewed your changes carefully and are ready to commit the
            changes to the prescription.
          </Typography>
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
            Changes to be saved:
          </Typography>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Old Value</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>New Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changes.map((change) => (
                  <TableRow key={change.field}>
                    <TableCell>{change.field}</TableCell>
                    <TableCell>{change.oldValue || 'N/A'}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                      {change.newValue}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        'Please confirm that you have reviewed your changes carefully and are ready to commit the changes to the prescription.'
      );

    showInfoDialog({
      title: 'Save Changes To Prescription',
      message: changeMessage,
      confirmButtonText: 'Yes',
      cancelable: true,
      callback: () => {
        setIsLoading(true);
        dispatch(
          overridePrescription({
            input: overridePrescriptionPayload,
            fromWorkQueue: isPrescriptionReview,
            isPrescriptionEdit,
            isFillEdit,
          })
        )
          .then((success) => {
            if (!isEmpty(success)) {
              enqueueSnackbar('Prescription/Fill updated successfully', {
                variant: 'success',
              });
              setPayload({});
              clearHistory();

              // Re-fetch override audit details so the editing user is shown
              // immediately without requiring a manual page refresh (POM-5083).
              if (isPrescriptionEdit && onRefreshOverrideDetails) {
                onRefreshOverrideDetails();
              }

              if (!isPrescriptionReview) {
                setIsRefreshing(true);
                Promise.all([
                  dispatch(listPatientPrescriptions({ mpi })),
                  dispatch(getAllOrders({ mpi, includeAllFacilities: true })),
                  dispatch(
                    getPatientPrescriptionDetails({
                      mpi,
                      prescriptionUUID: prescriptionId,
                      forceRefresh: true,
                    })
                  ),
                ])
                  .then(() => {
                    setIsRefreshing(false);
                  })
                  .catch(() => {
                    setIsRefreshing(false);
                  });
              }
            } else {
              enqueueSnackbar('Error updating prescription/fill', { variant: 'error' });
            }
          })
          .finally(() => setIsLoading(false));
      },
    });
  };

  const getCurrentRefillValue = () => {
    if (Number(prescription?.refillsRemaining) === 0) {
      return 'N/A';
    }
    if (isPrescriptionReview && isFirstFillOrder) {
      return `1/${Number(updatedRefills)}`;
    }
    return `${Number(updatedRefills) - Number(prescription?.refillsRemaining) + 1}/${Number(
      updatedRefills
    )}`;
  };

  const getDispensedRefillValue = () => {
    if (isPrescriptionReview && isFirstFillOrder) {
      return `0/${Number(updatedRefills)}`;
    }
    return `${Number(updatedRefills) - Number(prescription?.refillsRemaining)}/${Number(
      updatedRefills
    )}`;
  };

  const handleFillHistoryClick = () => {
    setLoadingFillHistory(true);
    setOpenFillHistoryDialog(true);
    dispatch(getPrescriptionFillHistory({ prescriptionId }))
      .then((fillHistory) => {
        setFillHistoryData(fillHistory);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load fill history', { variant: 'error' });
        setOpenFillHistoryDialog(false);
      })
      .finally(() => {
        setLoadingFillHistory(false);
      });
  };

  return (
    <>
      {isRefreshing && <LoadingModule />}

      {!isRefreshing && (
        <>
          <style>
            {`
        @media print {
          .EditIcon {
            display: none;
          }

          .EditOrder {
            display: none;
          }

          .SaveChanges {
            display: none;
          }

          .ActionButtons {
            display: none;
          }

          .CopyIconButton {
            display: none;
          }
        }
      `}
          </style>

          {enableDrugEditOptions &&
            (showCatalogAssociation ? (
              <AssociateToCatalogModal
                open={resolveModal}
                onClose={closeResolveModal}
                prescriptionId={prescriptionId}
                lineItemId={lineItemId}
                drugName={drugData.drug}
                strength={drugData.strength}
                productId={drugData.productId}
              />
            ) : (
              <ResolveDrugProfileModal
                prescriptionId={prescriptionId}
                open={resolveModal}
                onClose={closeResolveModal}
                drugData={drugData}
                isEditLabel={isPrescriptionReview}
                patientId={mpi}
                reviewType={reviewType}
                lineItemId={lineItemId}
              />
            ))}

          <Card
            sx={{
              mb: 3,
              mt: 0,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: 'background.paper',
            }}
            elevation={3}
          >
            <CardContent sx={{ '&:last-child': { pb: 1, pt: 1 } }}>
              <Grid container spacing={2} alignItems='flex-start'>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      Prescription #{prescription?.rxNumber || 'N/A'}
                    </Typography>
                    <StatusBadge status='Active' />
                  </Box>
                  <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='caption' sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                        Created:
                        {prescription?.dateOrdered
                          ? format(new Date(prescription.dateOrdered), 'MM/dd/yyyy')
                          : 'N/A'}
                        , Electronically Transmitted
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='caption' sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                        Order Sent:
                        {prescription?.dateOrdered
                          ? `${format(
                              new Date(prescription.dateOrdered),
                              'MM/dd/yyyy hh:mm a'
                            )} ${momentTimeZone.tz.guess()}`
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='caption' sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                        Order Number: {prescription?.orderId || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='caption' sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                        Docutrack: {prescription?.documentId || 'N/A'}
                      </Typography>
                    </Grid>
                    {prescription?.refillsAuthorized && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant='caption' sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                          Current Refill: {getCurrentRefillValue()}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Typography variant='caption' sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                        Partner Name: {prescription?.partnerName || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={4} spacing={0.5}>
                  <Typography
                    variant='subtitle2'
                    sx={{ fontWeight: 600, mb: 1, fontSize: '0.8rem' }}
                  >
                    Patient
                  </Typography>
                  <Grid container spacing={0}>
                    <Grid item xs={12} lg={6} sx={{ pb: 0.5 }}>
                      <Typography variant='caption' sx={{ fontSize: '0.8rem' }}>
                        Name:
                        {patientInfo?.middleName
                          ? `${patientInfo?.firstName} ${patientInfo?.middleName} ${patientInfo?.lastName}`
                          : `${patientInfo?.firstName} ${patientInfo?.lastName}`}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} lg={6} sx={{ pb: 0.5 }}>
                      <Typography variant='caption' sx={{ fontSize: '0.8rem' }}>
                        DOB:
                        {patientInfo?.dob ? moment(patientInfo.dob).format('MM-DD-YYYY') : 'N/A'} (
                        {capitalize(patientInfo?.gender || '')})
                      </Typography>
                    </Grid>
                    <Grid item xs={12} lg={6} sx={{ pb: 0.5 }}>
                      <Typography
                        variant='caption'
                        sx={{
                          fontSize: '0.8rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        Email:{' '}
                        <PartnerContactValue
                          partnerContact={prescriptionPartnerContact}
                          contactKind='email'
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} lg={6} sx={{ pb: 0.5 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
                      >
                        <Typography variant='caption' sx={{ fontSize: '0.8rem' }}>
                          Patient ID:
                        </Typography>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
                        >
                          <Typography variant='caption' sx={{ fontSize: '0.8rem' }}>
                            {patientInfo?.mpi || 'N/A'}
                          </Typography>
                          {patientInfo?.mpi && <CopyIconButton text={patientInfo?.mpi} />}
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} lg={6} sx={{ pb: 0.5 }}>
                      <Typography variant='caption' sx={{ fontSize: '0.85rem' }}>
                        Phone:{' '}
                        <PartnerContactValue
                          partnerContact={prescriptionPartnerContact}
                          contactKind='phone'
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <Typography
                        variant='caption'
                        sx={{
                          fontSize: '0.85rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        Address: {patientInfo?.address?.addressLine1 || 'N/A'}
                        {patientInfo?.address?.addressLine2 &&
                          `, ${patientInfo.address.addressLine2}`}
                        {patientInfo?.address?.city &&
                          `, ${patientInfo.address.city}, ${patientInfo.address.state} ${patientInfo.address.zip}`}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={4}
                  container
                  justifyContent='flex-end'
                  alignItems='flex-start'
                  spacing={0.5}
                  className='ActionButtons'
                >
                  {isPrescriptionsTab && !isOtcPrescription && (
                    <Grid item sx={{ mr: 1 }}>
                      <RefillButton
                        prescriptionUUID={prescriptionId}
                        patientId={mpi}
                        rxNumber={prescription?.rxNumber}
                        prescribedQuantity={updatedQuantity}
                        orders={orders}
                      />
                    </Grid>
                  )}

                  {(reviewType === PrescriptionReviewStatus.PRESCRIPTION_REVIEW ||
                    showPrescriptionReview ||
                    (isPrescriptionsTab && hasSpecialEditUserAccess)) &&
                    !isOtcPrescription && (
                      <Grid item sx={{ mr: 1 }}>
                        <LoadingButton
                          variant='contained'
                          color='primary'
                          onClick={handleSavePrescriptionChanges}
                          className='SaveChanges'
                          disabled={isEmpty(omit(payload, ['patientId', 'prescriptionUUID']))}
                          loading={isLoading}
                          size='small'
                          sx={{
                            px: 1,
                            minHeight: 30,
                            fontSize: '0.75rem',
                            lineHeight: 'normal',
                            minWidth: 'auto',
                            textTransform: 'none',
                          }}
                        >
                          Save Changes
                        </LoadingButton>
                      </Grid>
                    )}

                  {onPrint && (
                    <Grid item sx={{ mr: 1 }}>
                      <IconButton
                        onClick={onPrint}
                        aria-label='Print Prescription'
                        sx={{ padding: '6px' }}
                      >
                        <PrintIcon sx={{ fontSize: '1.25rem' }} />
                      </IconButton>
                    </Grid>
                  )}
                  {onClose && (
                    <Grid item>
                      <IconButton
                        onClick={onClose}
                        aria-label='Close Prescription'
                        sx={{ padding: '6px' }}
                      >
                        <CloseIcon sx={{ fontSize: '1.25rem' }} />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Collapse
            appear
            in={
              !alertDismissed &&
              isSelfPrescribeMatchByParts(
                patientInfo?.lastName && patientInfo?.firstName
                  ? `${patientInfo.lastName}, ${patientInfo.firstName}`
                  : null,
                originalPrescriber?.firstName,
                originalPrescriber?.lastName
              )
            }
          >
            <Alert
              severity='warning'
              onClose={() => setAlertDismissed(true)}
              sx={{
                mb: 2,
                mx: 'auto',
                width: 'fit-content',
                backgroundColor: '#fff3cd',
                color: '#664d03',
                border: '1px solid #ffc107',
              }}
            >
              {SELF_PRESCRIBE_WARNING(prescription?.rxNumber)}
            </Alert>
          </Collapse>

          {reviewReason && (
            <Card sx={{ mb: 3, mx: 2 }} elevation={3}>
              <CardContent>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography
                    variant='subtitle1'
                    sx={{ fontWeight: 'bold', display: 'flex', fontSize: '0.875rem' }}
                  >
                    <Box component='span' sx={{ color: 'black' }}>
                      Review Reason:&nbsp;
                    </Box>
                    <Box component='span' sx={{ color: 'red' }}>
                      {reviewReason}
                    </Box>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {hasOverrideRecord && (
                      <>
                        <Button
                          variant='outlined'
                          color='warning'
                          onClick={() => setOpenChangeReasonDialog(true)}
                          size='small'
                          sx={{
                            minHeight: 30,
                            fontSize: '0.75rem',
                            lineHeight: 'normal',
                            minWidth: 'auto',
                            textTransform: 'none',
                          }}
                        >
                          Change Reason
                        </Button>
                        <ResolveAsIs
                          patientId={mpi}
                          lineItemId={lineItemId}
                          prescriptionUUID={prescriptionId}
                          sx={{ mr: 0 }}
                        />
                      </>
                    )}
                    {reviewType &&
                      reviewType !== PrescriptionReviewStatus.DUPLICATE_REVIEW &&
                      reviewType !==
                        PrescriptionReviewStatus.COMPOUNDING_STATE_NOT_LICENSED_REVIEW &&
                      prescription?.fillType === 'FIRST_FILL' && (
                        <ModifyItemButton
                          rxNumber={prescription?.rxNumber}
                          patientId={patientInfo.mpi}
                          lineItemId={lineItemId}
                          instructions={instructions}
                          prescriptionId={prescriptionId}
                          prescriptionData={prescriptionData}
                        />
                      )}
                    {reviewType ===
                      PrescriptionReviewStatus.COMPOUNDING_STATE_NOT_LICENSED_REVIEW && (
                      <Button
                        variant='contained'
                        size='small'
                        onClick={() => setShowResolveCentralFillModal(true)}
                      >
                        Resolve
                      </Button>
                    )}
                    {reviewType === PrescriptionReviewStatus.SHIP_LATER_REVIEW && (
                      <Button
                        variant='contained'
                        size='small'
                        sx={{
                          minHeight: 30,
                          fontSize: '0.75rem',
                          lineHeight: 'normal',
                          minWidth: 'auto',
                          textTransform: 'none',
                        }}
                        onClick={() => setIsResolveShipLaterOpen(true)}
                      >
                        Resolve
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          <InfoDialog />
          <FillHistoryDialog
            open={openFillHistoryDialog}
            onClose={() => setOpenFillHistoryDialog(false)}
            fillHistory={fillHistoryData}
            loading={loadingFillHistory}
            onUpdateFillHistory={() => {
              setLoadingFillHistory(true);
              dispatch(getPrescriptionFillHistory({ prescriptionId }))
                .then((fillHistory) => {
                  setFillHistoryData(fillHistory);
                })
                .catch(() => {
                  enqueueSnackbar('Failed to reload fill history', { variant: 'error' });
                })
                .finally(() => {
                  setLoadingFillHistory(false);
                });
            }}
          />

          <Grid container spacing={3} sx={{ pl: 2, pr: 2 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ mb: 3 }} elevation={3}>
                <CardContent>
                  <Typography variant='h6' sx={{ fontWeight: 600, mb: 2, fontSize: '0.85rem' }}>
                    Prescriber & Practice
                  </Typography>

                  <Grid container spacing={0} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant='subtitle2' sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        Prescriber
                      </Typography>
                    </Grid>
                    {showTranslation &&
                      (prescription?.overriddenPrescriber ||
                        prescriptionOverride?.prescriptionDetails?.prescriberName) && (
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant='subtitle2'
                            sx={{ fontWeight: 600, fontSize: '0.8rem' }}
                          >
                            Prescriber Override
                          </Typography>
                        </Grid>
                      )}
                  </Grid>

                  {(() => {
                    const hasOverride =
                      showTranslation &&
                      (prescription?.overriddenPrescriber ||
                        prescriptionOverride?.prescriptionDetails?.prescriberName);
                    const displayPrescriberName = updatedPrescriberName || overriddenPrescriberName;
                    return (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Grid container>
                          <Grid
                            item
                            xs={12}
                            md={hasOverride ? 6 : 12}
                            sx={{
                              borderRight: hasOverride ? '1px solid' : 'none',
                              borderColor: 'divider',
                              p: 1.5,
                            }}
                          >
                            <Typography variant='body2' sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                              Prescriber
                            </Typography>
                            <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                              {originalPrescriberName || 'N/A'}
                            </Typography>
                          </Grid>
                          {hasOverride && (
                            <Grid
                              item
                              xs={12}
                              md={6}
                              sx={{ p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                  Prescriber
                                </Typography>
                                <OverrideBadge />
                              </Box>
                              <Typography variant='body2' sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                                {displayPrescriberName || 'N/A'}
                              </Typography>
                              {overrideDetails?.prescriberName?.user &&
                                overrideDetails?.prescriberName?.editedTimestamp && (
                                  <Typography
                                    variant='caption'
                                    sx={{
                                      color: 'text.secondary',
                                      display: 'block',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Modified by: {overrideDetails.prescriberName.user} •{' '}
                                    {moment(overrideDetails.prescriberName.editedTimestamp).format(
                                      'MM/DD/YYYY hh:mm:ss A'
                                    )}
                                  </Typography>
                                )}
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    );
                  })()}

                  {(() => {
                    const hasOverride =
                      showTranslation &&
                      (prescription?.overriddenPrescriber ||
                        prescriptionOverride?.prescriptionDetails?.prescriberName);
                    const displayNpi = overriddenNpi || originalNpi;
                    const showNoChangesBadge =
                      hasOverride &&
                      overriddenNpi === originalNpi &&
                      !prescriptionOverride?.prescriptionDetails?.prescriberNpi;
                    return (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Grid container>
                          <Grid
                            item
                            xs={12}
                            md={hasOverride ? 6 : 12}
                            sx={{
                              borderRight: hasOverride ? '1px solid' : 'none',
                              borderColor: 'divider',
                              p: 1.5,
                            }}
                          >
                            <Typography variant='body2' sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                              NPI
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                {originalNpi || 'N/A'}
                              </Typography>
                              {practice?.verificationStatus && (
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    '& svg': {
                                      fontSize: '1.5rem',
                                    },
                                  }}
                                >
                                  <VerifiedTag
                                    status={VerificationStatus[practice.verificationStatus]}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Grid>
                          {hasOverride && (
                            <Grid
                              item
                              xs={12}
                              md={6}
                              sx={{ p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                  NPI
                                </Typography>
                                {showNoChangesBadge ? <NoChangesBadge /> : <OverrideBadge />}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                  {displayNpi || 'N/A'}
                                </Typography>
                                {overriddenPrescriber?.verificationStatus && (
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      '& svg': { fontSize: '0.85rem' },
                                    }}
                                  >
                                    <VerifiedTag
                                      status={
                                        VerificationStatus[overriddenPrescriber.verificationStatus]
                                      }
                                    />
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    );
                  })()}

                  {(() => {
                    const hasOverride =
                      showTranslation &&
                      (prescription?.overriddenPrescriber ||
                        prescriptionOverride?.prescriptionDetails?.prescriberName);
                    const displayPhone = overriddenPhone || originalPhone;
                    const showNoChangesBadge = hasOverride && overriddenPhone === originalPhone;
                    return (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Grid container>
                          <Grid
                            item
                            xs={12}
                            md={hasOverride ? 6 : 12}
                            sx={{
                              borderRight: hasOverride ? '1px solid' : 'none',
                              borderColor: 'divider',
                              p: 1.5,
                            }}
                          >
                            <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                              Phone
                            </Typography>
                            <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                              {originalPhone || 'N/A'}
                            </Typography>
                          </Grid>
                          {hasOverride && (
                            <Grid
                              item
                              xs={12}
                              md={6}
                              sx={{ p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                  Phone
                                </Typography>
                                {showNoChangesBadge ? <NoChangesBadge /> : <OverrideBadge />}
                              </Box>
                              <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                {displayPhone || 'N/A'}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    );
                  })()}

                  {/* POM-6354: the address is the practice address on both sides of an override, so
                      it is shown once instead of as an original/overridden comparison. */}
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      p: 1.5,
                    }}
                  >
                    <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                      Address
                    </Typography>
                    <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                      {formatAddress(originalAddress)}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant='subtitle2'
                      sx={{ fontWeight: 600, mb: 1.5, fontSize: '0.85rem' }}
                    >
                      Practice Information
                    </Typography>
                    <Box>
                      <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                        Practice:
                      </Typography>
                      <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 1.5 }}>
                        {practice?.practiceName || ''}
                      </Typography>
                      <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                        Practice ID:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          {practice?.practiceId || 'N/A'}
                        </Typography>
                        {practice?.practiceId && <CopyIconButton text={practice.practiceId} />}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ mb: 3 }} elevation={3}>
                <CardContent>
                  <Typography variant='h6' sx={{ fontWeight: 600, mb: 2, fontSize: '0.85rem' }}>
                    Pharmacy Information
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#E0E0E0',
                      borderRadius: '12px',
                      p: 0.3,
                      mb: 2,
                      width: '100%',
                    }}
                  >
                    <Tabs
                      value={pharmacyTab}
                      onChange={(e, newValue) => {
                        setPharmacyTab(newValue);
                        setTransferPharmacyIndex(0);
                      }}
                      sx={{
                        width: '100%',
                        minHeight: 'auto',
                        '& .MuiTabs-indicator': {
                          display: 'none',
                        },
                        '& .MuiTabs-flexContainer': {
                          gap: 0.5,
                        },
                      }}
                    >
                      <Tab
                        label='Transfer'
                        sx={{
                          flex: 1,
                          backgroundColor: pharmacyTab === 0 ? '#FFFFFF' : 'transparent',
                          color: '#000000',
                          fontWeight: 600,
                          borderRadius: '12px',
                          boxShadow: pharmacyTab === 0 ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                          textTransform: 'none',
                          minHeight: '36px',
                          py: 0.5,
                          transition: 'all 0.2s ease',
                        }}
                      />
                      <Tab
                        label='Originating'
                        sx={{
                          flex: 1,
                          backgroundColor: pharmacyTab === 1 ? '#FFFFFF' : 'transparent',
                          color: '#000000',
                          fontWeight: 600,
                          borderRadius: '12px',
                          boxShadow: pharmacyTab === 1 ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                          textTransform: 'none',
                          minHeight: '36px',
                          py: 0.5,
                          transition: 'all 0.2s ease',
                        }}
                      />
                    </Tabs>
                  </Box>

                  {pharmacyTab === 0 &&
                    (() => {
                      const transferPharmacies = prescription?.transferPharmacies || [];
                      const currentTransferPharmacy = transferPharmacies[transferPharmacyIndex];
                      const transferPharmacyAddress = currentTransferPharmacy?.pharmacyAddress;
                      const hasMultipleTransfers = transferPharmacies.length > 1;

                      if (!currentTransferPharmacy) {
                        return (
                          <Typography
                            variant='body2'
                            sx={{ fontSize: '0.8rem', color: 'text.secondary' }}
                          >
                            No transfer pharmacy information available
                          </Typography>
                        );
                      }

                      return (
                        <>
                          {hasMultipleTransfers && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 2,
                              }}
                            >
                              <Typography
                                variant='body2'
                                sx={{ fontSize: '0.85rem', color: 'text.secondary' }}
                              >
                                Transfer {transferPharmacyIndex + 1} of {transferPharmacies.length}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  size='small'
                                  onClick={() =>
                                    setTransferPharmacyIndex((prev) =>
                                      prev > 0 ? prev - 1 : transferPharmacies.length - 1
                                    )
                                  }
                                  disabled={transferPharmacies.length <= 1}
                                >
                                  <ChevronLeftIcon fontSize='small' />
                                </IconButton>
                                <IconButton
                                  size='small'
                                  onClick={() =>
                                    setTransferPharmacyIndex((prev) =>
                                      prev < transferPharmacies.length - 1 ? prev + 1 : 0
                                    )
                                  }
                                  disabled={transferPharmacies.length <= 1}
                                >
                                  <ChevronRightIcon fontSize='small' />
                                </IconButton>
                              </Box>
                            </Box>
                          )}
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ mb: 1.5 }}>
                                <Typography
                                  variant='body2'
                                  sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                                >
                                  Pharmacy:
                                </Typography>
                                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                  {currentTransferPharmacy?.name || 'N/A'}
                                </Typography>
                              </Box>
                              <Box sx={{ mb: 1.5 }}>
                                <Typography
                                  variant='body2'
                                  sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                                >
                                  Pharmacist:
                                </Typography>
                                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                  {currentTransferPharmacy?.pharmacistName || 'N/A'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant='body2'
                                  sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                                >
                                  Address:
                                </Typography>
                                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                  {transferPharmacyAddress?.addressLine1 || 'N/A'}
                                  {transferPharmacyAddress?.addressLine2 &&
                                    `, ${transferPharmacyAddress.addressLine2}`}
                                  {transferPharmacyAddress &&
                                    `, ${transferPharmacyAddress?.addressCity || ''}, ${
                                      transferPharmacyAddress?.addressState || ''
                                    } ${transferPharmacyAddress?.addressZip || ''}`}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ mb: 1.5 }}>
                                <Typography
                                  variant='body2'
                                  sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                                >
                                  NPI:
                                </Typography>
                                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                  {currentTransferPharmacy?.npi || 'N/A'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant='body2'
                                  sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                                >
                                  Transfer Date:
                                </Typography>
                                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                  {currentTransferPharmacy?.transferDate
                                    ? format(
                                        new Date(currentTransferPharmacy.transferDate),
                                        'MM/dd/yyyy hh:mmaaa'
                                      )
                                    : 'N/A'}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </>
                      );
                    })()}

                  {pharmacyTab === 1 && originatingPharmacy && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant='body2'
                            sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                          >
                            Pharmacy:
                          </Typography>
                          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                            {originatingPharmacy?.name || 'N/A'}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography
                            variant='body2'
                            sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                          >
                            Pharmacist:
                          </Typography>
                          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                            {originatingPharmacy?.pharmacistName || 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant='body2'
                            sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                          >
                            Address:
                          </Typography>
                          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                            {originatingPharmacyAddress?.addressLine1 || 'N/A'}
                            {originatingPharmacyAddress?.addressLine2 &&
                              `, ${originatingPharmacyAddress.addressLine2}`}
                            {originatingPharmacyAddress &&
                              `, ${originatingPharmacyAddress?.addressCity || ''}, ${
                                originatingPharmacyAddress?.addressState || ''
                              } ${originatingPharmacyAddress?.addressZip || ''}`}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography
                            variant='body2'
                            sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                          >
                            NPI:
                          </Typography>
                          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                            {originatingPharmacy?.npi || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} sx={{ alignSelf: 'flex-start' }}>
              <Card
                sx={{
                  mb: 3,
                  height: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 'unset',
                }}
                elevation={3}
              >
                <CardContent
                  sx={{ flex: '1 1 auto', p: 1.5, '&:last-child': { pb: 1.5 }, minHeight: 'unset' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      Prescription Details
                    </Typography>
                    {(reviewType === PrescriptionReviewStatus.PRESCRIPTION_REVIEW ||
                      showPrescriptionReview ||
                      (isPrescriptionsTab && hasSpecialEditUserAccess)) &&
                      !isOtcPrescription &&
                      fillType === OrderFillType.FIRST_FILL &&
                      !isReshipment && (
                        <Tooltip title={getEditPrescriptionTooltip()} arrow>
                          <span>
                            <Grid sx={{ pt: 0.5 }}>
                              <Button
                                variant='outlined'
                                color='primary'
                                onClick={openEditPrescriptionModal}
                                className='EditOrder'
                                disabled={isEditPrescriptionDisabled}
                                size='small'
                                sx={{
                                  px: 1,
                                  minHeight: 30,
                                  fontSize: '0.75rem',
                                  lineHeight: 'normal',
                                  minWidth: 'auto',
                                  textTransform: 'none',
                                }}
                              >
                                Edit Prescription
                              </Button>
                            </Grid>
                          </span>
                        </Tooltip>
                      )}
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={2}>
                      <Typography
                        variant='body2'
                        sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                      >
                        Rx Number
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          {prescription?.rxNumber || 'N/A'}
                        </Typography>
                        {prescription?.rxNumber && <CopyIconButton text={prescription.rxNumber} />}
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Typography
                        variant='body2'
                        sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                      >
                        Date Written
                      </Typography>
                      <Typography variant='body2' sx={{ fontSize: '0.85rem' }}>
                        {prescription?.writtenDate
                          ? moment(prescription.writtenDate).format('MM-DD-YYYY')
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Typography
                        variant='body2'
                        sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                      >
                        Last Fill Date
                      </Typography>
                      <Typography variant='body2' sx={{ fontSize: '0.85rem' }}>
                        {prescription?.lastFillDate
                          ? moment(prescription.lastFillDate).format('MM-DD-YYYY')
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Typography
                        variant='body2'
                        sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                      >
                        Days Supply
                      </Typography>
                      <Typography variant='body2' sx={{ fontSize: '0.85rem' }}>
                        {prescription?.daysSupply || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Typography
                        variant='body2'
                        sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                      >
                        Expiration Date
                      </Typography>
                      <Typography variant='body2' sx={{ fontSize: '0.85rem' }}>
                        {prescription?.expirationDate
                          ? moment(prescription.expirationDate).format('MM-DD-YYYY')
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Typography
                        variant='body2'
                        sx={{ fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}
                      >
                        DAW
                      </Typography>
                      <Typography variant='body2' sx={{ fontSize: '0.85rem' }}>
                        {prescription?.daw || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ mb: 3 }} />

                  {/* POM-5748: the catalog product this prescription is associated with — shown
                      above the medication comparison since it is what drives fulfillment. The
                      dispensed detail rows fill in once the prescription is fulfilled. */}
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1.5,
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant='subtitle2'
                      sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.25 }}
                    >
                      Product ID : {prescriptionProductId || 'N/A'}
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}
                    >
                      This prescription is currently associated with the Product ID
                    </Typography>

                    {fulfilledProduct?.ndc && (
                      <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12} md={4}>
                          <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                            Dispensed NDC
                          </Typography>
                          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                            {fulfilledProduct.ndc}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                            Dispensed Drug
                          </Typography>
                          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                            {[fulfilledProduct.drugName, fulfilledProduct.strength]
                              .filter(Boolean)
                              .join(' ') || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                            Manufacturer
                          </Typography>
                          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                            {fulfilledProduct.manufacturer || 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </Box>

                  <Grid container spacing={0} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant='subtitle2' sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        Prescription as Written
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}
                      >
                        Original from prescriber
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant='subtitle2' sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        Prescription to Dispense
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}
                      >
                        Final verified for dispensing
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Grid container>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                          borderRight: '1px solid',
                          borderColor: 'divider',
                          p: 1.5,
                        }}
                      >
                        <Typography variant='body2' sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                          Medication
                        </Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          {prescription?.drug || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{ p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                            Medication
                          </Typography>
                          {updatedDrug !== prescription?.drug && <OverrideBadge />}
                          {updatedDrug === prescription?.drug && <NoChangesBadge />}
                          {enableDrugEditOptions && !isOtcPrescription && (
                            <IconButton
                              onClick={() => setResolveModal(true)}
                              size='small'
                              className='EditIcon'
                              sx={{ padding: '2px' }}
                              aria-label='Edit Medication'
                            >
                              <EditIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          )}
                        </Box>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                          {updatedDrug}
                        </Typography>
                        {updatedDrug !== prescription?.drug &&
                          overrideDetails?.drugProfile?.user && (
                            <Typography
                              variant='caption'
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                                fontSize: '0.8rem',
                              }}
                            >
                              Modified by: {overrideDetails.drugProfile.user} •
                              {overrideDetails.drugProfile.editedTimestamp
                                ? moment(overrideDetails.drugProfile.editedTimestamp).format(
                                    'MM/DD/YYYY hh:mm:ss A'
                                  )
                                : 'N/A'}
                            </Typography>
                          )}
                      </Grid>
                    </Grid>
                  </Box>

                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Grid container>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                          borderRight: '1px solid',
                          borderColor: 'divider',
                          p: 1.5,
                        }}
                      >
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          Strength
                        </Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          {prescription?.strength || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{ p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                            Strength
                          </Typography>
                          {updatedStrength !== prescription?.strength && <OverrideBadge />}
                          {updatedStrength === prescription?.strength && <NoChangesBadge />}
                        </Box>
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          {updatedStrength}
                        </Typography>
                        {updatedStrength !== prescription?.strength &&
                          overrideDetails?.drugProfile?.user && (
                            <Typography
                              variant='caption'
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                                fontSize: '0.8rem',
                              }}
                            >
                              Modified by: {overrideDetails.drugProfile.user} •
                              {overrideDetails.drugProfile.editedTimestamp
                                ? moment(overrideDetails.drugProfile.editedTimestamp).format(
                                    'MM/DD/YYYY hh:mm:ss A'
                                  )
                                : 'N/A'}
                            </Typography>
                          )}
                      </Grid>
                    </Grid>
                  </Box>

                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Grid container>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                          borderRight: '1px solid',
                          borderColor: 'divider',
                          p: 1.5,
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                              Drug Form
                            </Typography>
                            <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                              {prescription?.form || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                              Units
                            </Typography>
                            <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                              {prescription?.unit || 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{ p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                Drug Form
                              </Typography>
                              {updatedForm !== (prescription?.form || 'NA') && <OverrideBadge />}
                              {updatedForm === (prescription?.form || 'NA') && <NoChangesBadge />}
                            </Box>
                            <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                              {updatedForm}
                            </Typography>
                            {updatedForm !== (prescription?.form || 'NA') &&
                              overrideDetails?.drugProfile?.user &&
                              overrideDetails?.drugProfile?.editedTimestamp && (
                                <Typography
                                  variant='caption'
                                  sx={{
                                    color: 'text.secondary',
                                    display: 'block',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  Modified by: {overrideDetails.drugProfile.user} •
                                  {moment(overrideDetails.drugProfile.editedTimestamp).format(
                                    'MM/DD/YYYY hh:mm:ss A'
                                  )}
                                </Typography>
                              )}
                          </Grid>
                          <Grid item xs={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                Units
                              </Typography>
                              {updatedUnit !== (prescription?.unit || 'NA') && <OverrideBadge />}
                              {updatedUnit === (prescription?.unit || 'NA') && <NoChangesBadge />}
                            </Box>
                            <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                              {updatedUnit}
                            </Typography>
                            {updatedUnit !== (prescription?.unit || 'NA') &&
                              overrideDetails?.drugProfile?.user &&
                              overrideDetails?.drugProfile?.editedTimestamp && (
                                <Typography
                                  variant='caption'
                                  sx={{
                                    color: 'text.secondary',
                                    display: 'block',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  Modified by: {overrideDetails.drugProfile.user} •
                                  {moment(overrideDetails.drugProfile.editedTimestamp).format(
                                    'MM/DD/YYYY hh:mm:ss A'
                                  )}
                                </Typography>
                              )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Grid container>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                          borderRight: '1px solid',
                          borderColor: 'divider',
                          p: 1.5,
                        }}
                      >
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          Prescribed Quantity
                        </Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          {prescription?.quantityPrescribed || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{ p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                            Prescribed Quantity
                          </Typography>
                          {(prescriptionOverride?.prescriptionDetails?.quantity ||
                            payload?.prescriptionDetails?.quantity) && <OverrideBadge />}
                          {!prescriptionOverride?.prescriptionDetails?.quantity &&
                            !payload?.prescriptionDetails?.quantity && <NoChangesBadge />}
                        </Box>
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          {updatedQuantity}
                        </Typography>
                        {(prescriptionOverride?.prescriptionDetails?.quantity ||
                          payload?.prescriptionDetails?.quantity) &&
                          overrideDetails?.quantity?.user &&
                          overrideDetails?.quantity?.editedTimestamp && (
                            <Typography
                              variant='caption'
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                                fontSize: '0.8rem',
                              }}
                            >
                              Modified by: {overrideDetails.quantity.user} •
                              {moment(overrideDetails.quantity.editedTimestamp).format(
                                'MM/DD/YYYY hh:mm:ss A'
                              )}
                            </Typography>
                          )}
                      </Grid>
                    </Grid>
                  </Box>

                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Grid container>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                          borderRight: '1px solid',
                          borderColor: 'divider',
                          p: 1.5,
                        }}
                      >
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          Refills
                        </Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          {prescription?.refillsAuthorized || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{ p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                            Refills
                          </Typography>
                          {(prescriptionOverride?.prescriptionDetails?.refills ||
                            payload?.prescriptionDetails?.refills) && <OverrideBadge />}
                          {updatedRefills === prescription?.refillsAuthorized &&
                            !prescriptionOverride?.prescriptionDetails?.refills &&
                            !payload?.prescriptionDetails?.refills && <NoChangesBadge />}
                        </Box>
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          {updatedRefills}
                        </Typography>
                        {(prescriptionOverride?.prescriptionDetails?.refills ||
                          payload?.prescriptionDetails?.refills) &&
                          overrideDetails?.refills?.user &&
                          overrideDetails?.refills?.editedTimestamp && (
                            <Typography
                              variant='caption'
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                                fontSize: '0.8rem',
                              }}
                            >
                              Modified by: {overrideDetails.refills.user} •
                              {moment(overrideDetails.refills.editedTimestamp).format(
                                'MM/DD/YYYY hh:mm:ss A'
                              )}
                            </Typography>
                          )}
                      </Grid>
                    </Grid>
                  </Box>

                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Grid container>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                          borderRight: '1px solid',
                          borderColor: 'divider',
                          p: 1.5,
                        }}
                      >
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          Directions
                        </Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          {instructions || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{ p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                            Directions (SIG)
                          </Typography>
                          {(prescriptionOverride?.sig?.sigText || payload?.sig?.sig) && (
                            <OverrideBadge />
                          )}
                          {enableSigEditOptions && !isOtcPrescription && (
                            <IconButton
                              onClick={() => setResolveDirection(true)}
                              size='small'
                              className='EditIcon'
                              sx={{ padding: '2px' }}
                              aria-label='Edit Directions'
                            >
                              <EditIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          )}
                        </Box>
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          {payload?.sig?.sig ||
                            prescriptionOverride?.sig?.sigText ||
                            instructions ||
                            'N/A'}
                        </Typography>
                        {(prescriptionOverride?.sig?.sigText || payload?.sig?.sig) &&
                          overrideDetails?.sig?.user && (
                            <Typography
                              variant='caption'
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                                fontSize: '0.8rem',
                              }}
                            >
                              Modified by: {overrideDetails.sig.user} •
                              {overrideDetails.sig.editedTimestamp
                                ? moment(overrideDetails.sig.editedTimestamp).format(
                                    'MM/DD/YYYY hh:mm:ss A'
                                  )
                                : 'N/A'}
                            </Typography>
                          )}
                      </Grid>
                    </Grid>
                  </Box>

                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box>
                      <Typography
                        variant='subtitle2'
                        sx={{ fontWeight: 600, mb: 1, fontSize: '0.85rem' }}
                      >
                        Prescriber Notes
                      </Typography>
                      <Typography variant='body2' sx={{ fontSize: '0.85rem' }}>
                        {prescription.rxNotes || 'N/A'}
                      </Typography>
                    </Box>
                  </>
                </CardContent>
              </Card>

              <Card sx={{ mb: 3 }} elevation={3}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      Fill history & Current fill
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {(reviewType === PrescriptionReviewStatus.PRESCRIPTION_REVIEW ||
                        showPrescriptionReview ||
                        (isPrescriptionsTab && hasSpecialEditUserAccess)) &&
                        !isOtcPrescription && (
                          <Tooltip title={getEditFillTooltip()} arrow>
                            <span>
                              <Button
                                variant='outlined'
                                color='primary'
                                onClick={openEditFillModal}
                                className='EditOrder'
                                disabled={isEditFillDisabled}
                                size='small'
                                sx={{
                                  px: 1,
                                  minHeight: 30,
                                  fontSize: '0.75rem',
                                  lineHeight: 'normal',
                                  minWidth: 'auto',
                                  textTransform: 'none',
                                }}
                              >
                                Edit Current Fill
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      <Button
                        variant='outlined'
                        onClick={handleFillHistoryClick}
                        disabled={loadingFillHistory}
                        size='small'
                        sx={{
                          px: 1,
                          minHeight: 30,
                          fontSize: '0.75rem',
                          lineHeight: 'normal',
                          minWidth: 'auto',
                          textTransform: 'none',
                        }}
                      >
                        {loadingFillHistory ? <CircularProgress size={20} /> : 'View Fill history'}
                      </Button>
                    </Box>
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                      }}
                    >
                      <Typography variant='body2' sx={{ fontSize: '0.85rem' }}>
                        Current Fill Type:
                      </Typography>
                      <StatusBadge
                        status={fillType === OrderFillType.FIRST_FILL ? 'First Fill' : 'Refill'}
                      />
                    </Box>
                    <Divider />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                      }}
                    >
                      <Typography variant='body2' sx={{ fontSize: '0.85rem' }}>
                        Quantity in Current Fill:
                      </Typography>
                      <Typography variant='body2' sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                        {updatedPrescriptionQuantity || 'N/A'}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                      }}
                    >
                      <Typography variant='body2' sx={{ fontSize: '0.85rem' }}>
                        Total Quantity Dispensed:
                      </Typography>
                      <Typography variant='body2' sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                        {prescription?.totalDispensedQuantity || 'N/A'}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                      }}
                    >
                      <Typography variant='body2' sx={{ fontSize: '0.85rem' }}>
                        Refills dispensed:
                      </Typography>
                      <Typography variant='body2' sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                        {getDispensedRefillValue()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3} sx={{ alignSelf: 'flex-start' }}>
              {notes !== undefined && onAddNote && (
                <Card sx={{ mb: 3 }} elevation={3}>
                  <CardContent>
                    <PatientPrescriptionNotes notes={notes} onAddNote={onAddNote} />
                  </CardContent>
                </Card>
              )}

              <Card
                sx={{
                  height: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 'unset',
                }}
                elevation={3}
              >
                <CardContent
                  sx={{ flex: '1 1 auto', p: 1.5, '&:last-child': { pb: 1.5 }, minHeight: 'unset' }}
                >
                  <Typography variant='h6' sx={{ fontWeight: 600, mb: 2, fontSize: '0.9rem' }}>
                    Evidence
                  </Typography>

                  <EvidenceSection
                    prescriptionId={prescriptionId}
                    attestation={prescription?.attestation}
                    attachments={prescription?.attachments}
                    dispatch={dispatch}
                    prescription={prescription}
                    originalPrescriber={originalPrescriber}
                    practice={practice}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {hasOverrideRecord && (
            <ChangeReviewReasonDialog
              open={openChangeReasonDialog}
              patientId={mpi}
              onClose={() => setOpenChangeReasonDialog(false)}
              lineItem={lineItemId}
              prescriptionId={prescriptionId}
              orderId={prescription?.orderId}
              drugName={updatedDrug}
              strength={updatedStrength}
              form={updatedForm}
              drugUnit={updatedUnit}
              firstName={patientData?.firstName}
              lastName={patientData?.lastName}
              dob={patientData?.dob}
              email={patientData?.email}
              gender={patientData?.gender}
            />
          )}

          {enableDrugEditOptions &&
            (showCatalogAssociation ? (
              <AssociateToCatalogModal
                open={resolveModal}
                onClose={closeResolveModal}
                prescriptionId={prescriptionId}
                lineItemId={lineItemId}
                drugName={updatedDrug}
                strength={updatedStrength}
              />
            ) : (
              <ResolveDrugProfileModal
                prescriptionId={prescriptionId}
                open={resolveModal}
                onClose={closeResolveModal}
                drugData={{
                  drug: updatedDrug,
                  strength: updatedStrength,
                  form: updatedForm,
                  unit: updatedUnit,
                }}
                isEditLabel={isPrescriptionReview}
                patientId={mpi}
                reviewType={reviewType}
                lineItemId={lineItemId}
              />
            ))}

          {enableSigEditOptions && (
            <ResolveDirectionModal
              open={resolveDirection}
              onClose={closeDirectionResolveModal}
              drug={updatedDrug}
              directions={prescriptionDirection}
              form={updatedForm}
              strength={updatedStrength}
              prescriptionId={prescriptionId}
              isEditLabel={isPrescriptionReview}
              patientId={mpi}
              reviewType={reviewType}
              lineItemId={lineItemId}
              reviewReason={directionReview?.reviewReason}
              currentSigId={directionReview?.currentSigId}
              currentSig={directionReview?.currentSig}
            />
          )}

          {(reviewType === PrescriptionReviewStatus.PRESCRIPTION_REVIEW ||
            showPrescriptionReview ||
            hasSpecialEditUserAccess) && (
            <>
              <EditPrescriptionModal
                mpi={mpi}
                open={showEditPrescriptionModal}
                onClose={closeEditPrescriptionModal}
                currentRefill={currentRefill}
                practiceId={practice?.practiceId}
                editablePrescriptionDetails={{
                  providerId,
                  prescriberName: updatedPrescriberName,
                  prescribedQuantity: updatedQuantity,
                  refills: updatedRefills,
                  dispensedQuantity: updatedPrescriptionQuantity,
                }}
              />

              <EditFillModal
                patientId={mpi}
                open={showEditFillModal}
                prescriptionUUID={prescriptionId}
                onClose={closeEditFillModal}
                editablePrescriptionDetails={editablePrescriptionDetails}
                currentRefill={currentRefill}
                shippedDate={prescription?.activeItemShippedDate}
                isReshipment={isReshipment}
                parentLineItemQuantity={prescription?.parentLineItemQuantity}
              />
            </>
          )}

          {reviewType && (
            <MultiRxReviewDialog
              prescriptionId={prescriptionId}
              open={showResolveMultiRxModal}
              onClose={closeMultiRxModal}
              orderId={prescription?.orderId}
              fillType={fillType}
              isSplitRx
            />
          )}
        </>
      )}
      {showResolveCentralFillModal && (
        <ResolveCentralFillStateModal
          open={showResolveCentralFillModal}
          onClose={() => setShowResolveCentralFillModal(false)}
          lineItemId={lineItemId}
          mpi={mpi}
          orderState={matchedOrder?.shippingAddress?.state}
        />
      )}
      {isResolveShipLaterOpen && (
        <ResolveShipLaterReviewModal
          open={isResolveShipLaterOpen}
          onClose={() => setIsResolveShipLaterOpen(false)}
          orderId={prescription?.orderId}
          lineItemId={lineItemId}
          patientId={mpi}
        />
      )}
    </>
  );
};

export default PatientPrescriptionView;

const editedFieldPropTypes = PropTypes.shape({
  user: PropTypes.string,
  editedTimestamp: PropTypes.string,
});

PatientPrescriptionView.propTypes = {
  isPrescriptionsTab: PropTypes.bool,
  mpi: PropTypes.string,
  prescriptionId: PropTypes.string,
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      fillType: PropTypes.string,
      lineItems: PropTypes.arrayOf(
        PropTypes.shape({
          lineItemId: PropTypes.string,
          quantity: PropTypes.string,
        })
      ),
    })
  ),
  prescriptionData: PropTypes.shape({
    practice: PropTypes.shape({
      practiceName: PropTypes.string,
      practiceId: PropTypes.string,
      prescriberName: PropTypes.string,
      electronicallySignedBy: PropTypes.string,
      supervisor: PropTypes.string,
      prescriberDea: PropTypes.string,
      prescriberNpi: PropTypes.string,
      orderingAgent: PropTypes.string,
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
    writtenDate: PropTypes.string,
    dispensedQuantity: PropTypes.string,
    expirationDate: PropTypes.string,
    lastFillDate: PropTypes.string,
    dateOrdered: PropTypes.string,
    orderId: PropTypes.string,
    drug: PropTypes.string,
    strength: PropTypes.string,
    form: PropTypes.string,
    rxNotes: PropTypes.string,
    rxNumber: PropTypes.string,
    quantityPrescribed: PropTypes.string,
    drugUnit: PropTypes.string,
    daysSupply: PropTypes.string,
    refillsRemaining: PropTypes.string,
    daw: PropTypes.string,
    prescriptionOverride: PropTypes.shape({
      prescriptionDetails: PropTypes.shape({
        prescriberName: PropTypes.string,
        quantity: PropTypes.string,
        refills: PropTypes.string,
      }),
      drugProfile: PropTypes.shape({
        drugName: PropTypes.string,
        strength: PropTypes.string,
      }),
      sig: PropTypes.shape({
        sigText: PropTypes.string,
      }),
    }),
    request: PropTypes.shape({
      medication: PropTypes.shape({
        prescriber: PropTypes.shape({
          phone: PropTypes.string,
          fax: PropTypes.string,
        }),
        instructions: PropTypes.string.isRequired,
      }),
    }),
  }),
  patientData: PropTypes.shape({
    qs1Id: PropTypes.string,
    middleName: PropTypes.string,
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    address: PropTypes.shape({
      addressLine1: PropTypes.string,
      addressLine2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
    dob: PropTypes.string,
    gender: PropTypes.string,
    mpi: PropTypes.string,
  }),
  overrideDetails: PropTypes.shape({
    prescriberName: editedFieldPropTypes,
    drugProfile: editedFieldPropTypes,
    quantity: editedFieldPropTypes,
    refills: editedFieldPropTypes,
    sig: editedFieldPropTypes,
  }),
  directionReview: PropTypes.shape({
    reviewReason: PropTypes.string,
    currentSigId: PropTypes.string,
    currentSig: PropTypes.string,
  }),
  isPrescriptionReview: PropTypes.bool,
  reviewType: PropTypes.string,
  showTranslation: PropTypes.bool.isRequired,
  lineItemId: PropTypes.string,
  onPrint: PropTypes.func,
  onClose: PropTypes.func,
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      noteId: PropTypes.string.isRequired,
      noteContent: PropTypes.string.isRequired,
      noteCreatedAt: PropTypes.string.isRequired,
      noteAuthor: PropTypes.shape({
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        middleName: PropTypes.string,
      }).isRequired,
    })
  ),
  onAddNote: PropTypes.func,
  onRefreshOverrideDetails: PropTypes.func,
};

PatientPrescriptionView.defaultProps = {
  isPrescriptionsTab: false,
  mpi: null,
  prescriptionId: null,
  prescriptionData: null,
  directionReview: null,
  patientData: null,
  overrideDetails: null,
  isPrescriptionReview: false,
  reviewType: '',
  lineItemId: null,
  orders: [],
  onPrint: null,
  onClose: null,
  notes: [],
  onAddNote: null,
  onRefreshOverrideDetails: null,
};

PatientPrescriptionView.propTypes = {
  mpi: PropTypes.string,
  prescriptionId: PropTypes.string,
  overrideDetails: PropTypes.shape({
    prescriberName: PropTypes.shape({ user: PropTypes.string, editedTimestamp: PropTypes.string }),
    drugProfile: PropTypes.shape({ user: PropTypes.string, editedTimestamp: PropTypes.string }),
    quantity: PropTypes.shape({ user: PropTypes.string, editedTimestamp: PropTypes.string }),
    refills: PropTypes.shape({ user: PropTypes.string, editedTimestamp: PropTypes.string }),
    sig: PropTypes.shape({ user: PropTypes.string, editedTimestamp: PropTypes.string }),
  }),
  lineItemId: PropTypes.string,
  onRefreshOverrideDetails: PropTypes.func,
};
